import { useEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description?: string;
  image?: string;
  url?: string;
  /** @deprecated Use `url` instead */
  canonical?: string;
  /** @deprecated Use `image` instead */
  ogImage?: string;
  type?: 'website' | 'article';
  noIndex?: boolean;
  /** @deprecated Use `noIndex` instead */
  noindex?: boolean;
  structuredData?: object;
}

export function SEOHead({
  title,
  description,
  image,
  url,
  canonical,
  ogImage,
  type = 'website',
  noIndex = false,
  noindex,
  structuredData,
}: SEOHeadProps) {
  useEffect(() => {
    // Set document title
    const fullTitle = `${title} | Invitatii Online Marketplace`;
    document.title = fullTitle;

    // Resolve backward-compatible props
    const resolvedUrl = url || canonical;
    const resolvedImage = image || ogImage;
    const resolvedNoIndex = noIndex || noindex || false;

    // Helper to set/update meta tag
    const setMetaTag = (name: string, content: string, property = false) => {
      const attr = property ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Set description
    if (description) {
      setMetaTag('description', description);
      setMetaTag('og:description', description, true);
      setMetaTag('twitter:description', description);
    }

    // Set Open Graph
    setMetaTag('og:title', fullTitle, true);
    setMetaTag('og:type', type, true);
    setMetaTag('og:site_name', 'Invitații Online', true);
    setMetaTag('og:locale', 'ro_RO', true);
    if (resolvedUrl) setMetaTag('og:url', resolvedUrl, true);
    if (resolvedImage) {
      setMetaTag('og:image', resolvedImage, true);
      setMetaTag('twitter:image', resolvedImage);
    }

    // Set Twitter card
    setMetaTag('twitter:card', resolvedImage ? 'summary_large_image' : 'summary');
    setMetaTag('twitter:title', fullTitle);
    setMetaTag('twitter:site', '@InvitatiiOnline');

    // Set robots
    if (resolvedNoIndex) {
      setMetaTag('robots', 'noindex, nofollow');
    }

    // Set canonical URL
    if (resolvedUrl) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'canonical';
        document.head.appendChild(link);
      }
      link.href = resolvedUrl;
    }

    // Set structured data (JSON-LD)
    if (structuredData) {
      const existingScript = document.querySelector('script[data-seo-head="structured-data"]');
      if (existingScript) {
        existingScript.textContent = JSON.stringify(structuredData);
      } else {
        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-seo-head', 'structured-data');
        script.textContent = JSON.stringify(structuredData);
        document.head.appendChild(script);
      }
    }

    // Cleanup function
    return () => {
      // Remove structured data script on unmount to prevent duplicates
      const script = document.querySelector('script[data-seo-head="structured-data"]');
      if (script) {
        script.remove();
      }
    };
  }, [title, description, image, url, canonical, ogImage, type, noIndex, noindex, structuredData]);

  return null; // This component doesn't render anything
}
